import type { SpeechEngine, SpeechEngineError } from './speechEngine';
import {
  applySpeechResult,
  dictationText,
  rebaseDictation,
  startDictation,
  type VoiceDictation,
} from './voiceDictation';

export const VOICE_INPUT_UNSUPPORTED_MESSAGE =
  "Voice input isn't supported in this browser (try Chrome or Edge)";

export function voiceInputErrorMessage(error: SpeechEngineError): string | null {
  switch (error.code) {
    case 'not-allowed':
      return 'Microphone access is blocked. Allow microphone access in your browser settings to use voice input.';
    case 'no-speech':
      return null;
    case 'network':
      return 'Voice input could not reach the speech service. Check your connection and try again.';
    case 'unsupported':
      return VOICE_INPUT_UNSUPPORTED_MESSAGE;
    default:
      return `Voice input stopped: ${error.message}`;
  }
}

export interface VoiceInputControllerOptions {
  engine: SpeechEngine;
  onText: (text: string) => void;
  onListeningChange: (isListening: boolean) => void;
  onError: (error: SpeechEngineError) => void;
}

export interface VoiceInputController {
  isSupported(): boolean;
  isListening(): boolean;
  start(base: string): void;
  stop(): void;
  discard(): void;
  toggle(base: string): void;
  rebase(text: string): void;
}

export function createVoiceInputController({
  engine,
  onText,
  onListeningChange,
  onError,
}: VoiceInputControllerOptions): VoiceInputController {
  let dictation: VoiceDictation | null = null;
  let listening = false;

  const setListening = (value: boolean) => {
    if (listening === value) return false;
    listening = value;
    onListeningChange(value);
    return true;
  };

  engine.onResult((result) => {
    if (!dictation) return;
    dictation = applySpeechResult(dictation, result);
    onText(dictationText(dictation));
  });
  engine.onEnd(() => {
    dictation = null;
    setListening(false);
  });
  engine.onError((error) => {
    dictation = null;
    if (setListening(false)) engine.stop();
    onError(error);
  });

  const start = (base: string) => {
    if (listening) return;
    if (!engine.isSupported()) {
      onError({ code: 'unsupported', message: VOICE_INPUT_UNSUPPORTED_MESSAGE });
      return;
    }
    dictation = startDictation(base);
    setListening(true);
    engine.start();
  };

  const stop = () => {
    if (setListening(false)) engine.stop();
  };

  return {
    isSupported: () => engine.isSupported(),
    isListening: () => listening,
    start,
    stop,
    discard: () => {
      dictation = null;
      stop();
    },
    toggle: (base) => (listening ? stop() : start(base)),
    rebase: (text) => {
      if (dictation) dictation = rebaseDictation(dictation, text);
    },
  };
}
